import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import AddIcon from '@mui/icons-material/Add';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { Grid } from '@mui/material';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import useConditionalWorkflowPathStore, { getConditionalWorkflowPathStore } from './useConditionalWorkflowPathStore';
import ItemLogic from '../ConditionalWorkflow/components/ItemLogic';

const ConditionalWorkflowPathTemplate = (props) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getConditionalWorkflowPathStore(props.component_id), [props.component_id]);
  const data = store(s => s.data);

  const insertPlaceholder = useCallback((label, text, style) => {
    const { placeholders, setPlaceholders } = store.getState();
    setPlaceholders([...placeholders, { label, text, style }]);
  }, []);

  useEffect(() => {
    if (props.component_id === undefined) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then(response => {
        if (response && response.data) {
          const { components } = response.data;
          if (components?.[0]?.componentData) {
            store.getState().setConditionalWorkflowTemplate(
              components[0].componentData
            );
            store.setState({ loaded: true });
            props.onComponentNameChanged(store.getState().name);
            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(store.getState().lastModified);
            setLoading(false);
          }
          setLoading(false);
          props.setChildTemplateLoaded(true);
        }
      })
      .catch(err => {
        throw Error(err.message);
      });
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder.selected) return;
        const { style, key } = placeholder.selected;
        insertPlaceholder(style.default, key, style);
      }
    );
    return unsub;
  }, [insertPlaceholder]);

  const handleKeyChanges = useCallback((err) => {
    setError(err);
  }, []);

  const addAnotherItem = useCallback(() => {
    const currentData = store.getState().data;
    const newData = [...currentData, store.getState().logicData()];
    store.getState().setData(newData);
  }, []);

  if (!loading) {
    return (
      <Grid container direction={'column'} xs={12}>
        {data.map((d, index) => (
          <ItemLogic
            isExpanded={props.isExpanded}
            key={d.comPId || index}
            data={d}
            indexLogic={index}
            availablePlaceholders={props.availablePlaceholders}
            handleKeyChanges={handleKeyChanges}
            conditionalWorkflowPath={true}
          />
        ))}
        <Grid
          xs={5}
          container
          direction={'row'}
          item
          className="text-button"
          alignItems={'center'}
          onClick={addAnotherItem}
        >
          <AddIcon /> Add Another Path
        </Grid>
      </Grid>
    );
  }
  return <IndividualComponentLoader />;
};

export default ConditionalWorkflowPathTemplate;
